import { z } from "zod";
import { E164 } from "../constants/phone.js";

/**
 * Building blocks reused by every domain schema. Keeping them here means the
 * console's form validation and the API's request validation are literally the
 * same object, so a client can never submit something the server will reject for
 * a reason the client could have known.
 */

/**
 * Every entity is keyed by a uuid: companies, sites, users, memberships,
 * visit types, assessment modules, appointments, visits and assessments. Nothing in this product shows an id to a
 * user - there is no "Visit #150" anywhere - so a sequential key bought only a
 * scrapeable row count and an enumerable URL.
 */
export const uuidParam = z.string().uuid();

export const trimmedString = (min: number, max: number) => z.string().trim().min(min).max(max);

/**
 * Path-parameter schemas, so `:id` is validated rather than coerced inside the
 * controller.
 *
 * `Number(req.params.id)` on `/sites/abc` is `NaN`, which PostgREST forwards and
 * Postgres rejects as `22P02` - a malformed URL reported to the caller as an
 * internal server error. A schema makes it the 400 it always was, and does it
 * before any query runs.
 */
export const idParams = z.object({ id: uuidParam });
export const siteIdParams = z.object({ siteId: uuidParam });

/**
 * IANA zone. Not validated against the full tz database - `Intl` is the arbiter
 * at render time, and a rejected-but-valid zone is a worse failure than a stored
 * typo that shows up immediately as a formatting error.
 */
export const timezone = z.string().trim().min(1).max(64).default("UTC");

/** URL-safe, lowercase, no leading/trailing separators. */
export const slug = z
  .string()
  .trim()
  .toLowerCase()
  .min(2)
  .max(48)
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, "Use lowercase letters, numbers and hyphens");

/**
 * A boolean in a query string.
 *
 * **Not `z.coerce.boolean()`.** That is `Boolean(value)`, so every non-empty
 * string is true and `?is_active=false` returns exactly the rows it was asked to
 * exclude - the only value that was ever false is the empty one. It fails
 * silently and looks correct in the schema, which is the worst combination.
 *
 * `api/src/config/env.ts` avoids the same trap the same way for
 * `SELF_SIGNUP_ENABLED`. Strict about the two spellings on purpose: a filter
 * that quietly ignores `?is_active=yes` is the bug one layer removed.
 */
export const booleanQuery = z.enum(["true", "false"]).transform((v) => v === "true");

/**
 * An instant, not a day.
 *
 * `offset: true` is the whole point: it rejects a bare `2026-08-17T09:00:00`,
 * which has no timezone and is therefore two different moments depending on who
 * parses it. Everything scheduled in this product is stored as `timestamptz` and
 * travels as an ISO instant; the *day* it falls on is a rendering question the
 * client answers against the site's zone.
 *
 * See `ui/src/lib/dates.ts` for why a "YYYY-MM-DD" is a different type entirely.
 */
export const isoDateTime = z.string().datetime({ offset: true });

export const paginationQuery = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
  search: z.string().trim().max(120).optional(),
});

export type PaginationQuery = z.infer<typeof paginationQuery>;

/**
 * Password policy. Length does the work; complexity classes mostly produce
 * `Password1!` and a sticky note. Supabase enforces its own minimum too
 * (`minimum_password_length` in config.toml, and the Auth settings on a hosted
 * project) - keep this at or above that value, or users hit a server error the
 * form said nothing about.
 */
/**
 * A phone number, stored as E.164 and nothing else.
 *
 * `+919876543210` - see `constants/phone.ts` for why. Both clients build this
 * from a country picker plus a national number, so a value that fails here is a
 * client bug rather than a typo somebody can fix by reading the message.
 *
 * **Empty is absent, not invalid.** Every phone field in this product is
 * optional, and an untouched input submits `""`; without the transform that
 * lands in the column as an empty string, which is neither a number nor null
 * and reads as "they gave us one" in every list that tests for presence.
 *
 * Existing rows hold free text from before this rule. They are not rewritten -
 * migrations do not seed and they do not repair - and the field parses them
 * back into a country and digits so the next edit stores E.164. See
 * `parsePhone`, which is deliberate about not discarding what it cannot parse.
 */
export const phoneE164 = z
  .string()
  .trim()
  .max(32)
  .refine((value) => value === "" || E164.test(value), "Enter a full phone number")
  .transform((value) => (value === "" ? undefined : value));

export const password = z.string().min(10, "Use at least 10 characters").max(128);

export const email = z.string().trim().toLowerCase().email();
